import React, { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';

import UTIF from '@column/utif';

import { convertCleanBase64 } from '~/util';

interface Props {
  src?: string;
  /**
   * Callback fired once the underlying image or canvas knows its intrinsic
   * dimensions. Useful for parents that need to pre-compute layout.
   */
  onSize?: (naturalWidth: number, naturalHeight: number) => void;
}

const BASE64_TIFF_DATA_PREFIX = 'data:image/tiff;base64';

const base64ToUint8Array = (base64: string) => {
  const binaryString = atob(base64);
  const length = binaryString.length;
  const bytes = new Uint8Array(length);

  for (let i = 0; i < length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return bytes;
};

const uint8ArrayToArrayBuffer = (uint8Array: Uint8Array): ArrayBuffer => {
  const buffer = new ArrayBuffer(uint8Array.length);
  const view = new Uint8Array(buffer);
  view.set(uint8Array);
  return buffer;
};

const isUrl = (str: string): boolean => {
  try {
    new URL(str);
    return true;
  } catch {
    return false;
  }
};

const isTiffData = (data: ArrayBuffer): boolean => {
  const view = new Uint8Array(data);
  // Check for TIFF magic numbers: 0x4949 (II) or 0x4d4d (MM) followed by 0x002a or 0x2b00
  if (view.length < 4) return false;

  const isLittleEndian = view[0] === 0x49 && view[1] === 0x49;
  const isBigEndian = view[0] === 0x4d && view[1] === 0x4d;

  if (!isLittleEndian && !isBigEndian) return false;

  if (isLittleEndian) {
    return (view[2] === 0x2a && view[3] === 0x00) || (view[2] === 0x2b && view[3] === 0x00);
  } else {
    return (view[2] === 0x00 && view[3] === 0x2a) || (view[2] === 0x00 && view[3] === 0x2b);
  }
};

const imageCache = new Map<string, Promise<{ data: ArrayBuffer; contentType: string }>>();

const fetchImageData = async (url: string): Promise<{ data: ArrayBuffer; contentType: string }> => {
  if (imageCache.has(url)) {
    return imageCache.get(url)!;
  }

  const promise = (async () => {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.statusText}`);
    }
    const contentType = response.headers.get('Content-Type') || 'application/octet-stream';
    const data = await response.arrayBuffer();
    return { data, contentType };
  })();

  imageCache.set(url, promise);
  return promise;
};

const StyledCanvas = styled.canvas`
  display: block;
  width: 100%;
  height: auto;
  object-fit: contain;
`;

const StyledImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
  object-fit: contain;
`;

export const ImageViewer: React.FC<Props> = ({ src, onSize }) => {
  const imageType = useMemo(() => {
    if (!src) {
      return 'none';
    }

    // Check if it's base64 encoded TIFF
    if (src.startsWith(BASE64_TIFF_DATA_PREFIX)) {
      return 'base64-tiff';
    }

    // Check if it's a URL
    if (isUrl(src)) {
      return 'url';
    }

    return 'regular';
  }, [src]);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [urlImgSrc, setUrlImgSrc] = React.useState<string | undefined>(undefined);

  useEffect(() => {
    if (!src || imageType === 'none' || imageType === 'regular') {
      return;
    }

    const renderTiff = (tiffArrayBuffer: ArrayBuffer) => {
      const ifds = UTIF.decode(tiffArrayBuffer);
      if (ifds.length === 0) {
        throw new Error('Failed to decode IFDs from image data');
      }
      UTIF.decodeImage(tiffArrayBuffer, ifds[0]);

      const firstImage = ifds[0];
      const rgba = UTIF.toRGBA8(firstImage);

      const canvas = canvasRef.current;
      if (!canvas) {
        throw new Error('No canvas found');
      }

      const context = canvas.getContext('2d');
      if (!context) {
        throw new Error('No 2d context found');
      }
      canvas.width = firstImage.width;
      canvas.height = firstImage.height;

      const imageData = new ImageData(new Uint8ClampedArray(rgba), firstImage.width, firstImage.height);
      context.putImageData(imageData, 0, 0);

      onSize?.(firstImage.width, firstImage.height);

      if (imageType === 'url') {
        canvas.style.display = 'block';
        const img = imgRef.current;
        if (img) {
          img.style.display = 'none';
        }
      }
    };

    const processImage = async () => {
      try {
        if (imageType === 'base64-tiff') {
          const cleanImageData = convertCleanBase64(src);
          if (!cleanImageData) {
            throw new Error('Failed to clean base64 data');
          }
          const tiffUint8Array = base64ToUint8Array(cleanImageData);
          const tiffArrayBuffer = uint8ArrayToArrayBuffer(tiffUint8Array);
          renderTiff(tiffArrayBuffer);
        } else if (imageType === 'url') {
          const { data, contentType } = await fetchImageData(src);
          if (isTiffData(data)) {
            renderTiff(data);
          } else {
            const blob = new Blob([data], { type: contentType });
            const objectUrl = URL.createObjectURL(blob);
            setUrlImgSrc(objectUrl);
          }
        }
      } catch (error) {
        console.error('Error rendering image:', error);
        if (imageType === 'url') {
          setUrlImgSrc(src); // Fallback to direct URL
          const canvas = canvasRef.current;
          const img = imgRef.current;
          if (canvas) {
            canvas.style.display = 'none';
          }
          if (img) {
            img.style.display = 'block';
          }
        }
      }
    };

    processImage();
  }, [src, imageType, onSize]);

  if (imageType === 'url') {
    return (
      <>
        <StyledCanvas ref={canvasRef} style={{ display: 'none' }} />
        <StyledImage
          ref={imgRef}
          src={urlImgSrc ?? ''}
          style={{ display: urlImgSrc ? 'block' : 'none' }}
          onLoad={(e) => onSize?.(e.currentTarget.naturalWidth, e.currentTarget.naturalHeight)}
        />
      </>
    );
  }

  return imageType === 'base64-tiff' ? (
    <StyledCanvas ref={canvasRef} />
  ) : (
    <StyledImage src={src} onLoad={(e) => onSize?.(e.currentTarget.naturalWidth, e.currentTarget.naturalHeight)} />
  );
};
